import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, extractError } from '@/lib/api.js';
import { ANOS_DO_VINCULO_DECLARADO, NOME_DA_RELACAO, RELACOES_DECLARADAS, vinculoDeclaradoVale, type RelacaoDeclarada } from '@shared/conflito.js';

/**
 * O que o conselheiro declara (etapa 12): as empresas em que é ou foi sócio,
 * administrador, empregado ou consultor. Ao vincular uma empresa nova, a
 * gestão confere esta lista contra os concorrentes dela.
 */

type Vinculo = { id: string; nome: string; cnpj: string | null; relacao: RelacaoDeclarada; desde: string | null; ate: string | null };
type Resposta = { confirmada_em: string | null; vinculos: Vinculo[] };

const campo = 'rounded-element border border-ha-border2 bg-transparent px-2 py-1.5 text-sm';
const botao = 'rounded-element px-3 py-1.5 text-[10px] font-bold uppercase text-black disabled:opacity-50';
const mes = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric', timeZone: 'UTC' }) : null);

export function DeclaracaoDeVinculos() {
  const qc = useQueryClient();
  const chave = ['conselheiro-vinculos-declarados'];
  const q = useQuery({ queryKey: chave, queryFn: async () => (await api.get<Resposta>('/conselheiro/vinculos-declarados')).data });
  const [nome, setNome] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [relacao, setRelacao] = useState<RelacaoDeclarada>('SOCIO');
  const [desde, setDesde] = useState('');
  const [ate, setAte] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const comum = {
    onSuccess: (d: Resposta) => {
      setErro(null);
      qc.setQueryData(chave, d);
    },
    onError: (e: unknown) => setErro(extractError(e).message),
  };
  const incluir = useMutation({
    mutationFn: async () =>
      (
        await api.post<Resposta>('/conselheiro/vinculos-declarados', {
          nome: nome.trim(),
          cnpj: cnpj.trim() || null,
          relacao,
          desde: desde ? `${desde}-01` : null,
          ate: ate ? `${ate}-01` : null,
        })
      ).data,
    ...comum,
    onSuccess: (d: Resposta) => {
      comum.onSuccess(d);
      setNome('');
      setCnpj('');
      setDesde('');
      setAte('');
    },
  });
  const tirar = useMutation({ mutationFn: async (id: string) => (await api.delete<Resposta>(`/conselheiro/vinculos-declarados/${id}`)).data, ...comum });
  const confirmar = useMutation({ mutationFn: async () => (await api.post<Resposta>('/conselheiro/vinculos-declarados/confirmar', {})).data, ...comum });

  if (q.isLoading) return <p className="text-sm text-ha-subtle">Carregando…</p>;
  if (q.isError || !q.data) return null;
  const d = q.data;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-ha-muted">
        Liste as empresas em que você é, ou foi nos últimos {ANOS_DO_VINCULO_DECLARADO} anos, sócio, administrador, empregado ou
        consultor. Se uma delas for concorrente de uma empresa que vai ser vinculada a você, a gestão do programa vê o conflito
        antes de vincular.
      </p>
      {d.vinculos.length === 0 ? (
        <p className="text-sm text-ha-subtle">Nenhum vínculo declarado.</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {d.vinculos.map((v) => (
            <li key={v.id} className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
              <span>
                <span className="font-medium text-ha-text">{v.nome}</span>{' '}
                <span className="text-xs text-ha-muted">
                  {NOME_DA_RELACAO[v.relacao]}
                  {v.desde ? ` · desde ${mes(v.desde)}` : ''}
                  {v.ate ? ` · até ${mes(v.ate)}` : ' · atual'}
                  {v.ate && !vinculoDeclaradoVale(new Date(v.ate), new Date()) ? ` · mais de ${ANOS_DO_VINCULO_DECLARADO} anos, não conta` : ''}
                </span>
              </span>
              <button type="button" className="text-xs text-ha-muted underline hover:text-ha-text" disabled={tirar.isPending} onClick={() => tirar.mutate(v.id)}>
                tirar
              </button>
            </li>
          ))}
        </ul>
      )}
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (nome.trim().length >= 2) incluir.mutate();
        }}
      >
        <label className="flex flex-col gap-1 text-xs text-ha-muted">
          Empresa
          <input className={`${campo} w-52`} value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome da empresa" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-ha-muted">
          CNPJ (opcional)
          <input className={`${campo} w-40`} value={cnpj} onChange={(e) => setCnpj(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-ha-muted">
          Como
          <select className={campo} value={relacao} onChange={(e) => setRelacao(e.target.value as RelacaoDeclarada)}>
            {RELACOES_DECLARADAS.map((r) => (
              <option key={r} value={r}>
                {NOME_DA_RELACAO[r]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-ha-muted">
          Desde
          <input className={campo} type="month" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-ha-muted">
          Até (vazio = atual)
          <input className={campo} type="month" value={ate} onChange={(e) => setAte(e.target.value)} />
        </label>
        <button type="submit" className={botao} style={{ background: 'var(--primary)' }} disabled={incluir.isPending || nome.trim().length < 2}>
          Incluir
        </button>
      </form>
      <div className="flex flex-wrap items-center gap-3 border-t border-ha-border2 pt-3">
        <button type="button" className={botao} style={{ background: 'var(--primary)' }} disabled={confirmar.isPending} onClick={() => confirmar.mutate()}>
          {d.confirmada_em ? 'Confirmar de novo' : 'Minha lista está completa'}
        </button>
        <span className="text-xs text-ha-muted">
          {d.confirmada_em
            ? `Confirmada em ${new Date(d.confirmada_em).toLocaleDateString('pt-BR')}. Mudou algo? Atualize e confirme de novo.`
            : 'Ainda não confirmada: a gestão do programa vê que a declaração está pendente.'}
        </span>
      </div>
      {erro ? <p className="text-sm text-red-600">{erro}</p> : null}
    </div>
  );
}
